import React, { useContext, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import PaymentFormErrorMessage from '@components/checkout/BillingPaymentFormErrorMessage';
import { Context } from '@components/Context';
import { NavContext } from '@components/navigation/Context';
import Button from '@components/shared/Button';
import { ButtonSize, CartItem, CartItemType, NavStatus, ProductCode } from '@contracts';
import { combineCartItemsAndAddOns, divideCartItems } from '@utils/cart';
import useNavigate from '@utils/useNavigate';

interface Props {
  cartItem?: CartItem | null;
  addonCartItems?: CartItem[];
  id?: string;
  className?: string;
  textKey?: string;
  buttonText?: string;
  backgroundColor?: string;
  buttonTextColor?: string;
  buttonSize?: ButtonSize;
  checkoutRedirect?: boolean;
  callback?: () => void;
  errorMessageCallback?: (msg: string) => void;
  redirectPath?: string;
}

const AddToCart: React.FC<Props> = ({
  cartItem,
  id,
  className,
  addonCartItems = [],
  textKey = 'components.add-to-cart',
  buttonText = '',
  buttonSize,
  backgroundColor = '',
  buttonTextColor = '',
  checkoutRedirect = false,
  callback = null,
  errorMessageCallback,
  redirectPath = '/checkout/'
}) => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const { state, dispatch } = useContext(Context);
  const { navDispatch } = useContext(NavContext);
  const { cartItems, country, currency } = state;
  const [errorText, setErrorText] = useState('');

  const setError = (error: string) => {
    if (errorMessageCallback) {
      errorMessageCallback(error);
    } else {
      setErrorText(error);
    }
  };

  const setErrorMessage = (error: string) => {
    setError(error);
    setTimeout(() => {
      setError('');
    }, 5000);
  };

  const checkCanAddToCart = () => {
    let message = '';

    if (!cartItem) {
      return false;
    }

    if (cartItems?.length) {
      const tamponSubsLimitReached = !!(
        cartItems.find((item) => item.productCode === ProductCode.TamponBox && item.type === CartItemType.Subscription) &&
        cartItem.productCode === ProductCode.TamponBox &&
        cartItem.type === CartItemType.Subscription
      );

      const padSubsLimitReached = !!(
        cartItems.find((item) => item.productCode === ProductCode.Pads && item.type === CartItemType.Subscription) &&
        cartItem.productCode === ProductCode.Pads &&
        cartItem.type === CartItemType.Subscription
      );

      if (tamponSubsLimitReached) {
        message = t('components.add-to-card-remove-tampons-subs-on-different');
      }

      if (padSubsLimitReached) {
        message = t('components.add-to-card-remove-pads-subs-on-different');
      }
    }

    if (cartItem.price < 0) {
      message = t('components.add-to-card-error-refresh');
    }

    if (message) {
      setErrorMessage(message);
      return false;
    }

    return true;
  };

  /**
   * Handles adding items to the shopping cart. It first checks if items can be added to the cart.
   * If so, it attempts to combine the main cart item and any add-on items into a single combined cart item,
   * until we have limitations where all of our products are prebuilt boxes.
   *
   * Remaining add-on items that couldn't be combined are dispatched individually. This is done to ensure
   * that even if certain add-on items do not meet the criteria to be combined with the main cart item
   * they are still added to the cart. Each of these remaining add-on items is treated as a separate
   * cart item, maintaining the integrity and modularity of the cart's state.
   *
   * Optionally, the function can trigger a checkout redirect or update navigation status based on the
   * operation's result. A callback function can also be invoked to perform additional operations after
   * the cart update process completes.
   *
   * @async
   * @function onAddToCart
   * @returns {Promise<void>}
   */
  const onAddToCart = async () => {
    const canAddToCart = checkCanAddToCart();

    if (canAddToCart && cartItem) {
      const { combinedCartItem, remainingAddOnItems } = await combineCartItemsAndAddOns(cartItem, addonCartItems, currency, country);

      if (combinedCartItem) {
        const devidedCartItems = divideCartItems(combinedCartItem, country);

        devidedCartItems.forEach((item) => {
          if (!item.unique || !cartItems.find((i) => i.id === item.id && i.type === item.type)) {
            dispatch({ type: 'cartItemAdd', data: item });
          }
        });
      }

      if (remainingAddOnItems) {
        remainingAddOnItems.forEach((item) => {
          dispatch({ type: 'cartItemAdd', data: item });
        });
      }

      if (checkoutRedirect) {
        navigateWrapper(redirectPath);
      } else {
        navDispatch({ type: 'setStatus', data: NavStatus.Basket });
      }
      if (callback) callback();
    }
  };

  return (
    <>
      <Button
        id={id ?? ''}
        disabled={cartItem?.price < 0}
        size={buttonSize}
        onClick={onAddToCart}
        className={className}
        backgroundColor={backgroundColor}
        buttonTextColor={buttonTextColor}
      >
        {buttonText !== '' ? buttonText : t(`${textKey}`)}
      </Button>
      {errorText && <PaymentFormErrorMessage errorMessage={errorText} />}
    </>
  );
};

export default AddToCart;
